import React, { useEffect, useState } from 'react';
import { shallowEqual } from 'react-redux';
import {
    WidgetArticleImageContent,
    WidgetArticleImageType,
    WidgetDisplayMode,
    formatImgUrl,
    jFileLittle,
} from 'jamespot-user-api';
import { useIntl } from 'react-intl';
import { Widget, updateWidgetContent } from 'jamespot-front-business';
import { WidgetUploader } from '../components/WidgetUploader';

const WidgetImage = commonComponentsRegistry.getLazyComponent('WidgetImage');
const FileViewer = commonComponentsRegistry.getLazyComponent('FileViewer');
const JRCWidgetEmptyInplace = commonComponentsRegistry.getLazyComponent('WidgetEmptyInplace');

import styled from 'styled-components';
import { commonComponentsRegistry } from '../../../registry/CommonRegistry';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

const CSSImageWrapperHover = styled.div`
    position: absolute;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.4);
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
    opacity: 0;
    color: ${(props) => props.theme.color.white};
    &:hover {
        opacity: 1;
    }
`;

const CSSImagePointer = styled.button`
    cursor: pointer;
`;

const mockFileLittle: jFileLittle = {
    id: 0,
    type: '',
    title: '',
    uri: '',
    mimetype: 'application/octet-stream',
    mainType: '',
    dateCreation: '',
    dateModified: '',
    _url: '',
    size: 10,
};

export const strToTimestamp = (str: string): undefined | number => {
    if (str) {
        const timestamp = Date.parse(str);
        if (!isNaN(timestamp)) {
            return new Date(timestamp).getTime() / 1000;
        }
    }
    return;
};

export const JRCWidgetArticleImageWrapper = ({
    widget,
    width,
    inplace,
    mode,
}: {
    widget: WidgetArticleImageType;
    inplace?: boolean;
    mode?: WidgetDisplayMode;
    width?: number;
}) => {
    const { uniqid } = widget;
    const { file, useFilter } = widget.content;
    const dispatch = useAppDispatch();
    const isActive = useAppSelector((state) => Widget.selectors.isActive(state, uniqid), shallowEqual);
    const [uri, setUri] = useState<string | undefined>(undefined);
    const intl = useIntl();

    useEffect(() => {
        dispatch(Widget.slice.actions.setWidgetState({ uniqid, mounted: true }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        dispatch(Widget.slice.actions.setWidgetState({ uniqid, empty: !file }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [file]);

    useEffect(() => {
        if (file) {
            setUri(
                formatImgUrl({
                    timestamp: file.dateModified ? strToTimestamp(file.dateModified) : Date.now(),
                    from: 'imagecache',
                    size: 'fitx1200',
                    uri: file.uri,
                }),
            );
        }
    }, [file]);

    return (
        <>
            {mode === 'edit' ? (
                uri !== undefined ? (
                    <WidgetImage maxWidth={width ?? 600} uri={uri} useFilter={useFilter ?? false} />
                ) : (
                    <></>
                )
            ) : (
                <FileViewer
                    files={[
                        {
                            ...mockFileLittle,
                            path: uri,
                            mimetype: 'image/png',
                        },
                    ]}
                    openWithOptions={[]}>
                    {(open) => {
                        return (
                            <CSSImagePointer onClick={() => open(0)}>
                                <WidgetImage maxWidth={width ?? 600} uri={uri} useFilter={useFilter ?? false} />
                            </CSSImagePointer>
                        );
                    }}
                </FileViewer>
            )}

            {inplace && mode === 'edit' ? (
                <WidgetUploader
                    initialFiles={[]}
                    initialClick={isActive}
                    accept={'image/png, image/jpeg, image/jpg'}
                    multiple={false}
                    onUploadSuccess={(files) => {
                        if (Array.isArray(files) && files[0] !== undefined) {
                            dispatch(updateWidgetContent<WidgetArticleImageContent>(uniqid, { file: files[0] }));
                        }
                    }}
                    onUploadStart={() => {
                        dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading: true }));
                    }}
                    onUploadEnd={() => {
                        dispatch(Widget.slice.actions.setWidgetState({ uniqid, loading: false }));
                    }}>
                    {file !== undefined ? (
                        <CSSImageWrapperHover>
                            {intl.formatMessage({ id: 'ARTICLE_Widget_Image_Change' })}
                        </CSSImageWrapperHover>
                    ) : (
                        <JRCWidgetEmptyInplace
                            label={'WIDGET_Article_Image_Empty_Label'}
                            description={'WIDGET_Article_Image_Empty_Description'}
                            url={'widget-article-image-placeholder.png'}
                            onClick={() => undefined}
                        />
                    )}
                </WidgetUploader>
            ) : (
                <></>
            )}
        </>
    );
};
